import { customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_EASM_CERTIFICATE_EXPIRATION } from '../../../../webcomponents.constants';
import { GzDonutChartWidgetWebComponent } from '../../../../ui/gzDonutChartWidget';
import { PropertyValues } from 'lit';
import {
  DonutChartWidgetSliceConfig, DonutChartWidgetSliceData,
  EasmCertificateExpirationPayload,
} from '@nirvana/shared/dtos/types/widgets/common';
import { mapCertificateExpiration } from '../types/types';
import { translateFn } from '../../../../utils/translator.helper';
import { store } from '../../../../redux/store';
import { selectDashboardFilterValues } from '../../../../genericDashboard/redux/dashboard.filters.slice';
import { GenericDashboardsFiltersHelper } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { generateFiltersForInventory } from '../../../utils/utils';
import { FilterType } from '../../genericTopData/types/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from 'apps/webcomponents/src/genericDashboard/gzGenericWidget/types';
/**
 * @summary This is a Web Component widget used in the EASM Dashboard. It extends the GzDonutChartWidgetWebComponent
 * class, which is based on the DonutChart implementation of the billboard.js library
 * https://naver.github.io/billboard.js/demo/#Chart.DonutChart
 * @tag gz-easm-certificate-expiration
 */
@customElement(WEB_COMPONENT_EASM_CERTIFICATE_EXPIRATION)
export class GzEasmCertificateExpirationWebComponent extends GzDonutChartWidgetWebComponent {
  private inventorySmartViewId: string;

  async updated(changedProperties: PropertyValues): Promise<void> {
    this.buildDataForWidget();
    await super.updated(changedProperties);
  }

  static get styles() {
    return styles;
  }

  private buildDataForWidget() {
    const slicesData: DonutChartWidgetSliceData[] = [];
    const slicesConfig: DonutChartWidgetSliceConfig[] = [];
    const certificateExpiration = mapCertificateExpiration(translateFn);

    if (this.payload) {
      const dataReceived = this.payload as unknown as EasmCertificateExpirationPayload;

      if (!dataReceived.easmCertificateExpiration) {
        if (this.payload?.donutChart?.config) {
          this.payload.donutChart.config.showTooltips = this.state === WidgetStateFrontEnd.IS_ACTIVE;
          this.payload.donutChart.config.disableSliceInteraction = this.state !== WidgetStateFrontEnd.IS_ACTIVE;
        }
        return;
      }

      this.inventorySmartViewId = dataReceived.inventorySmartViewId as string;

      dataReceived.easmCertificateExpiration.forEach(certificateStatus => {
        slicesConfig.push({
          id: certificateStatus.validity.toString(),
          label: certificateExpiration[certificateStatus.validity].label,
          color: certificateExpiration[certificateStatus.validity].color,
        });
        slicesData.push({ id: certificateStatus.validity.toString(), value: certificateStatus.count });
      });

      this.payload = {
        donutChart: {
          data: { slices: slicesData },
          config: {
            label: translateFn('certificates'),
            slices: slicesConfig,
            showTooltips: this.state === WidgetStateFrontEnd.IS_ACTIVE,
            disableSliceInteraction: this.state !== WidgetStateFrontEnd.IS_ACTIVE,
          },
        }
      };
    }
  }

  protected async handleChartLegendItemClick(
    sliceConfig: DonutChartWidgetSliceConfig,
    sliceData: DonutChartWidgetSliceData
  ): Promise<void> {
    if (this.state === WidgetStateFrontEnd.IS_ACTIVE) {
      await this.navigateToInventory(Number(sliceConfig.id));
    }
  }

  firstUpdated(): void {
    super.firstUpdated();
  }

  private async navigateToInventory(id: number): Promise<void> {
    const dashboardAppState = store.getState().dashboardsApp;
    const filters = selectDashboardFilterValues(dashboardAppState);
    const companyId = GenericDashboardsFiltersHelper.getCompanyIdFromSingleSelectCompanyFilter(filters) as string;
    const filtersToSend = generateFiltersForInventory(FilterType.EXPIRATION_STATUS, '', companyId, [id]);

    await NavigationHelper.navigateToEASMArtifactsInventory({
      smartViewId: this.inventorySmartViewId,
      filters: filtersToSend,
    });
  }

  public async handleTitleClick() {
    const dashboardAppState = store.getState().dashboardsApp;
    const filters = selectDashboardFilterValues(dashboardAppState);
    const companyId = GenericDashboardsFiltersHelper.getCompanyIdFromSingleSelectCompanyFilter(filters) as string;

    const filtersToSend = generateFiltersForInventory(FilterType.NONE, '', companyId, []);
    await NavigationHelper.navigateToEASMArtifactsInventory({
      smartViewId: this.inventorySmartViewId,
      filters: filtersToSend,
    });
  }
}
